/**
 * useFinanceAnalytics — EO «Финансы»: факт+прогноз за период (V1 FinAnalyticsView).
 *
 * GET /api/finance/analytics?from&to (perm finance:read, apps/api/src/routes/
 * finance.ts:62-206). ⚠ Сервер отвечает 200 с {error:"from/to required"} при
 * пустых границах — проверяем поле error, а не только catch. ⚠ estByBranch
 * приходит БЕЗ branch_id (Object.values дропает ключ) — мердж с byBranch
 * делается по branch_name (см. EoFinanceCharts). Разбор ответа (Number()||0 —
 * D1 может отдать null в COALESCE-дырах) — financeAnalytics.ts.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { normalizeAnalytics, type FinAnalytics, type RawAnalytics } from "./financeAnalytics";
/* Модель и разбор ответа — financeAnalytics.ts (чистый модуль, под тестом); типы экрану (EoFinanceCharts) — отсюда, как раньше. */
export type { FinAnalytics, FinCompleted, FinBranchRow, FinEstBranchRow, FinFutureBooked, FinDailyPoint, FinQuestRow, FinFuturePoint } from "./financeAnalytics";

export interface FinanceAnalyticsResult {
  data: FinAnalytics | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useFinanceAnalytics(from: string, to: string): FinanceAnalyticsResult {
  const [data, setData] = useState<FinAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    if (!from || !to) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);

    api<RawAnalytics>(`/api/finance/analytics?from=${from}&to=${to}`, { signal: ac.signal })
      .then((r) => {
        if (ac.signal.aborted) return;
        if (r?.error) { setError(r.error); setData(null); }
        else setData(normalizeAnalytics(r ?? {}));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : "Не удалось загрузить аналитику");
        setLoading(false);
      });

    return () => ac.abort();
  }, [from, to, trigger]);

  return { data, loading, error, reload };
}
